// Customer referral API client for the marketplace customer embed — TD-384
// T7 ("Refer & Save", migration 097); T18.5 (Ruling 13) adds the
// read-active-disclosure route (migration 099).
//
// Wire shape mirrors the Go model exactly (project rule §39):
//   model: internal/marketplace/model/referral.go (Referral)
//   handler: internal/marketplace/handler/customer_handler_referral.go
//     - HandleMyReferralsList (GET  /api/v1/marketplace/me/referrals) — own
//       rows, every status, optional ?status= filter
//     - HandleMyReferralSubmit (POST /api/v1/marketplace/me/referrals) —
//       submit, born submitted, version 1, expires_at server-computed
//   handler: internal/marketplace/handler/customer_handler_disclosure.go
//     - HandleMyActiveDisclosure (GET /api/v1/marketplace/me/referral-disclosure
//       ?kind=redemption_request) — T18.5: the kind's ACTIVE revision the
//       embed must SHOW before a redemption open; `is_active` is deliberately
//       absent from the wire (operator-only state — the row is active by
//       definition)
//   handler: internal/marketplace/handler/customer_handler_referral_balance.go
//     - HandleMyReferralBalance (GET /api/v1/marketplace/me/referral-balance)
//       — T26.5: the §4.4.1/§4.5 spendable+holding figure pair, decimal
//       strings, empty state "0.00"/"0.00"
//
// All handlers emit `{"data": <payload>}` — after embedApiFetch's .data
// unwrap, listMyReferrals() returns the array directly,
// submitReferral() and getActiveReferralDisclosure() return the struct
// directly (the mid-api.ts convention).
//
// The merchant's mutating routes are the initial submission AND the
// pending-redemption cancel (cancelMyRedemption — If-Match on version;
// §4.4.1: `pending` is the only merchant-cancellable status). Advance/
// complete/verify are operator-only (marketplace.referrals.write, the
// portal's admin surface).
// ⚠️ AMENDED 2026-10-04 (redesign Task 10 doc-comment sweep; the claim was
// already falsified on disk before this change):
//   ORIGINAL: "The merchant has NO mutating route beyond the initial
//   submission — advance/complete are operator-only
//   (marketplace.referrals.write, the portal's admin surface), so like
//   mid-api.ts there is no If-Match client here: nothing in this surface
//   ever mutates an existing referral."
//
// BINDING client constraint (carried from the T4 mid-api precedent):
// OMIT empty optional fields in the submit payload — never send
// `description: ""` or `street: ""`. The backend boundary does not
// normalize empty strings to NULL, so a cleared optional must send NO key.

import { embedApiFetch } from "../../embed-shared/api-client";

/**
 * One referral-pipeline row owned by the authenticated customer. Mirrors Go
 * model.Referral exactly (§39): pointer fields are `omitempty` on the wire,
 * so an unset optional arrives as an ABSENT key — `undefined` in TS, never a
 * value to coerce. Timestamps are RFC3339; completed_at is present iff
 * status is "completed" (the DB biconditional); expires_at is the referral
 * (NOT credit) expiry, server-computed as submitted_at +
 * settings.referral_expiry_months.
 */
export interface ReferralWire {
  id: string; // UUID
  referrer_customer_id: number;
  store_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
  contact_email: string;
  description?: string; // *string, omitempty — ruled optional
  street?: string; // *string, omitempty — ruled optional
  city: string; // ruled required
  state: string; // ruled required
  /** The referrer's claim of prior contact with the store (the form's
   *  checkbox; informational, not a gate). */
  prior_discussion: boolean;
  status: string; // submitted | in_queue | engaging | finalizing | completed | no_response | abandoned | declined | cancelled
  submitted_at: string; // RFC3339 — expires_at's anchor
  completed_at?: string; // *time.Time, omitempty — iff status === "completed"
  expires_at: string; // RFC3339
  target_customer_id?: number; // *int64, omitempty — set at completion
  version: number; // rule #10 OCC token (informational on this surface)
}

/** Request body for POST /api/v1/marketplace/me/referrals. The seven §4-required text fields are required (TD-610: no merchant account is named); description/street/prior_discussion are optional and OMITTED when blank. */
export interface SubmitReferralRequest {
  store_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
  contact_email: string;
  description?: string;
  street?: string;
  city: string;
  state: string;
  prior_discussion?: boolean;
  /** TD-765: the id of the ACTIVE `enrollment` disclosure revision the member accepted (getActiveReferralDisclosure("enrollment").id). Required by the server: missing = 422, superseded = 409 DISCLOSURE_STALE, none active = 503 DISCLOSURE_NOT_CONFIGURED. */
  enrollment_disclosure_id: string;
}

const REFERRALS_PATH = "/api/v1/marketplace/me/referrals";
const DISCLOSURE_PATH = "/api/v1/marketplace/me/referral-disclosure";
const REDEMPTIONS_PATH = "/api/v1/marketplace/me/referral-redemptions";
const BALANCE_PATH = "/api/v1/marketplace/me/referral-balance";

/**
 * The disclosure kinds the backend CHECK constraint admits — the TS mirror
 * of Go model.ValidReferralDisclosureKinds (§39). 'redemption_request' is the
 * text a merchant accepts to open a credit redemption; 'enrollment' is the
 * text accepted at referral submission.
 */
export type ReferralDisclosureKind = "redemption_request" | "enrollment";

/**
 * The kind's ACTIVE disclosure revision — the legal text the embed must SHOW
 * (and the merchant accept) BEFORE submitting a redemption open. Mirrors the
 * customer handler's wire shape exactly (§39): the admin ReferralDisclosure
 * row MINUS `is_active` (operator-only; this row is active by definition).
 * `id` is the value to echo as disclosure_id on the subsequent open — a
 * superseded id is a 409 DISCLOSURE_STALE at open, meaning the operator
 * swapped the text and the merchant must be shown this route's NEW result.
 */
export interface ActiveReferralDisclosureWire {
  id: string; // UUID — echo as disclosure_id on POST /me/referral-redemptions
  kind: ReferralDisclosureKind;
  revision: number; // CONTENT revision (per-kind 1, 2, 3…) — NOT the OCC token
  body: string; // the full legal text as shown
  effective_from: string; // RFC3339 — when this text became the terms of record
  version: number; // rule #10 OCC token (informational on this read-only surface)
}

/**
 * Fetch the kind's ACTIVE disclosure revision (T18.5: show-before-open).
 *
 * GET /api/v1/marketplace/me/referral-disclosure?kind=… → the
 * ActiveReferralDisclosureWire directly after the .data unwrap.
 *
 * Throws ApiError(400) for an unknown kind (membership, not a tag oracle),
 * ApiError(404, NOT_FOUND) when the kind has NO active revision yet (the
 * platform's legal-text configuration is incomplete — render "not available
 * yet", never fall back to arbitrary text), ApiError(401) when the customer
 * session is gone.
 */
export async function getActiveReferralDisclosure(
  kind: ReferralDisclosureKind,
): Promise<ActiveReferralDisclosureWire> {
  return embedApiFetch<ActiveReferralDisclosureWire>(DISCLOSURE_PATH, {
    query: { kind },
  });
}

/**
 * List the authenticated customer's own referrals — every status (own
 * history; eligibility gates govern submission and awards, never reads).
 * GET /api/v1/marketplace/me/referrals → ReferralWire[] directly after the
 * .data unwrap; an empty list arrives as data:[] and stays [].
 *
 * status filters to one of the nine CHECK values (400 otherwise).
 */
export async function listMyReferrals(status?: string): Promise<ReferralWire[]> {
  const result = await embedApiFetch<ReferralWire[]>(REFERRALS_PATH, {
    query: { status },
  });
  return result ?? [];
}

/**
 * Submit a referral (born submitted, version 1; expires_at is
 * server-computed — never send it).
 *
 * POST /api/v1/marketplace/me/referrals → the created ReferralWire.
 *
 * Blank optionals are OMITTED from the body entirely (never sent as "").
 *
 * Throws ApiError(403, CUSTOMER_TYPE_NOT_ALLOWED) when the caller is not a
 * buyer (design §3.8: a reseller or prospect type; a reseller's buyer
 * sub-customer MAY refer, S-5), ApiError(422) when the account owns no
 * Aldelo Pay store (a live store with an active MID linked to it) or a
 * required field is blank,
 * ApiError(503, REFERRAL_CONFIG_INVALID) when the program's expiry settings
 * are corrupt (platform problem, not the caller's). TD-765: ApiError(409,
 * DISCLOSURE_STALE) when enrollment_disclosure_id is no longer the active enrollment
 * revision (refetch, show the new text, require re-acceptance) and
 * ApiError(503, DISCLOSURE_NOT_CONFIGURED) when none is active.
 */
export async function submitReferral(
  req: SubmitReferralRequest,
): Promise<ReferralWire> {
  const body: Record<string, unknown> = {
    store_name: req.store_name.trim(),
    contact_first_name: req.contact_first_name.trim(),
    contact_last_name: req.contact_last_name.trim(),
    contact_phone: req.contact_phone.trim(),
    contact_email: req.contact_email.trim(),
    city: req.city.trim(),
    state: req.state.trim(),
    prior_discussion: req.prior_discussion ?? false,
    enrollment_disclosure_id: req.enrollment_disclosure_id,
  };
  if (req.description?.trim()) body.description = req.description.trim();
  if (req.street?.trim()) body.street = req.street.trim();
  return embedApiFetch<ReferralWire>(REFERRALS_PATH, { method: "POST", body });
}

// ─── Referral credit redemptions — the T18 customer routes (TD-384 T26) ────
//
// Wire shape mirrors the Go model exactly (§39); the wire-TYPE home per
// controller Ruling 4 is portal/api/marketplace.ts's ReferralRedemption
// (same Go model), mirrored here because the embed authenticates with a
// JWT Bearer (embedApiFetch), not the portal's cookie session:
//   model:   internal/marketplace/model/referral_redemption.go
//   handler: internal/marketplace/handler/customer_handler_redemption.go
//     - HandleMyRedemptionsList  (GET  /me/referral-redemptions[?status=])
//     - HandleMyRedemptionOpen   (POST /me/referral-redemptions)
//     - HandleMyRedemptionCancel (POST /me/referral-redemptions/{id}/cancel)

/**
 * One merchant redemption request. Mirrors Go model.ReferralRedemption
 * exactly (§39). Pointer fields are `omitempty` on the wire — an unset
 * optional arrives as an ABSENT key (`undefined` in TS; render both arms,
 * don't deref unconditionally).
 *
 * §30 from birth: `requested_amount` rides every row; `verified_amount` is
 * ABSENT until an employee verifies (never a manufactured "0" — treat
 * undefined and "0.00" distinctly: undefined means NOT YET VERIFIED).
 * `pending` is the ONLY merchant-cancellable status (§4.4.1 / Q3).
 */
export interface ReferralRedemptionWire {
  id: string; // UUID
  customer_id: number;
  mid_id: string; // the PROCESSING MID whose month is redeemed against
  statement_month: string; // RFC3339 first-of-month instant (Go time.Time) — render the YYYY-MM-DD prefix (T26-review rider)
  requested_amount: string; // decimal string — the provisional figure, reserved at open
  verified_amount?: string; // decimal string, ABSENT until verified (downward-only, Q10)
  status: string; // pending | in_progress | verified | applied | settled | rejected | cancelled | expired | revoked
  requested_at: string; // RFC3339 — pending-open instant; drives the 14-day sweep
  in_progress_at?: string; // present iff an employee picked the request up
  in_progress_by_user_id?: number;
  cancelled_at?: string; // present iff status === "cancelled" (DB biconditional)
  expired_at?: string;
  revoked_at?: string; // Q19 offset sweep (never a merchant action)
  offset_adjusted_at?: string;
  disclosure_id: string; // the ACTIVE revision accepted at open (§4.4.4)
  disclosure_accepted_at: string;
  disclosure_accepted_by_user_id: number;
  verified_by_user_id?: number;
  verified_at?: string;
  iris_statement_id?: string;
  verification_note?: string;
  verification_state: string; // not_started | queued | extracting | awaiting_review | confirmed | failed | skipped
  ai_model_id?: string;
  applied_at?: string;
  applied_by_user_id?: number;
  settled_at?: string;
  rejected_reason?: string;
  version: number; // rule #10 OCC token — cancel needs it as If-Match
}

/** Request body for POST /me/referral-redemptions — exactly three keys;
 * the server derives statement_month, stamps the disclosure acceptance
 * triple, and sets status itself. `disclosure_id` MUST be the id of the
 * revision the embed SHOWED and the merchant accepted
 * (getActiveReferralDisclosure's result). */
export interface OpenRedemptionRequest {
  // EXACTLY ONE of mid_id / store_id (TD-605; the server answers 422 for both
  // or neither). mid_id: the caller's own ACTIVE MID (§4.5 availability + Q6).
  // store_id: the caller's own store -- the server resolves its single ACTIVE
  // account, or answers 422 ACCOUNT_CHOICE_REQUIRED when it has several.
  mid_id?: string;
  store_id?: string;
  requested_amount: string; // decimal string, > 0, at most 2 decimal places
  disclosure_id: string; // echo of the SHOWN active revision's id
}

/**
 * List the caller's own redemption requests — every status, newest
 * requested_at first, no pagination. An empty list arrives as data:[] and
 * stays []. `status` filters to one of the nine CHECK values (400
 * otherwise). Every row surfaces BOTH money columns from birth (§30 —
 * verified_amount arrives as an ABSENT key until it exists).
 */
export async function listMyRedemptions(status?: string): Promise<ReferralRedemptionWire[]> {
  const result = await embedApiFetch<ReferralRedemptionWire[]>(REDEMPTIONS_PATH, {
    query: { status },
  });
  return result ?? [];
}

// ─── Redeem-only store list (merge-review M2, TD-586) ───────────────────────
//
// Operator answer 2026-10-10: "Redeem-only store list (Recommended)" -- "A new
// endpoint under the redeem permission returns just the account's stores (name
// and whether a merchant account is linked), enough to pick one. It grants no
// store editing."
//   handler: internal/marketplace/handler/customer_handler_store.go
//     HandleMyReferralRedeemStores (GET /me/referral-redemption-stores, area
//     participate-referrals). Go model: model.RedeemStore.
const REDEEM_STORES_PATH = "/api/v1/marketplace/me/referral-redemption-stores";

/** The redeem-only store projection (Go model.RedeemStore): just enough to
 *  pick a store. Deliberately NOT CustomerStoreWire -- no address, phone,
 *  version or merchant (MID) number is sent to a member who may only redeem. */
export interface RedeemStoreWire {
  id: string;
  name: string;
  /** True when at least one LIVE, ACTIVE merchant account is linked to the
   *  store -- the dialog's "no merchant account linked" message keys on false. */
  has_active_merchant_account: boolean;
}

/**
 * GET /me/referral-redemption-stores -- the caller's own stores for the redeem
 * dialog, under participate-referrals (a member who can redeem credits but
 * cannot manage stores gets this list; GET /me/stores would 403 them). Returns
 * [] when the account has no stores (the server answers `data: []`, never
 * null). Throws ApiError on any failure -- a 403 means the role lacks
 * participate-referrals and is surfaced, not turned into an empty list.
 */
export async function listRedeemStores(): Promise<RedeemStoreWire[]> {
  const result = await embedApiFetch<RedeemStoreWire[]>(REDEEM_STORES_PATH);
  return result ?? [];
}

/**
 * Open a redemption request (born pending, version 1): reserves
 * requested_amount against the caller's credit balance.
 *
 * NOT idempotency-keyed server-side — the DB uniques make a double-submit
 * a 409 (REDEMPTION_ALREADY_IN_FLIGHT / REDEMPTION_MONTH_USED), the
 * strongest double-submit guard (the POST /me/mids reasoning).
 *
 * Throws ApiError(403, CUSTOMER_TYPE_NOT_ALLOWED) for callers that are not a
 * direct buyer with an Aldelo Pay store, 404 for a mid_id that is unknown or another customer's
 * (deliberately indistinguishable), 422 for an inactive MID / a bad amount
 * shape / insufficient balance, 409 DISCLOSURE_STALE when the active
 * disclosure changed since the client showed it (re-fetch and re-accept),
 * 409 for the in-flight/month uniques, 503 DISCLOSURE_NOT_CONFIGURED.
 */
export async function openMyRedemption(
  req: OpenRedemptionRequest,
): Promise<ReferralRedemptionWire> {
  return embedApiFetch<ReferralRedemptionWire>(REDEMPTIONS_PATH, {
    method: "POST",
    body: {
      ...(req.store_id !== undefined ? { store_id: req.store_id } : { mid_id: req.mid_id }),
      requested_amount: req.requested_amount,
      disclosure_id: req.disclosure_id,
    },
  });
}

/**
 * Cancel a PENDING, never-picked-up request (§4.4.1: the only cancellable
 * state) and release the full reservation back to the balance. If-Match
 * REQUIRED (428 absent, 409 stale); §59: re-seed the row from the response
 * (version+1). A retried completed cancel gets 422
 * REDEMPTION_NOT_CANCELLABLE or 409 — the version+state guard IS the
 * idempotency, no key attached.
 */
export async function cancelMyRedemption(
  id: string,
  version: number,
): Promise<ReferralRedemptionWire> {
  return embedApiFetch<ReferralRedemptionWire>(
    `${REDEMPTIONS_PATH}/${id}/cancel`,
    { method: "POST", headers: { "If-Match": String(version) } },
  );
}

// ─── Referral balance — the T26.5 customer read (TD-384, Ruling 16) ────────
//
// GET /api/v1/marketplace/me/referral-balance → the §4.4.1/§4.5 figure pair
// the RedemptionsPanel summary shows as "Available credit" and passes to
// RedeemDialog as the amount gate's visible denominator (Task 10: the
// dialog receives it as a prop; the always-mounted RedeemPanel panel it
// formerly decorated is gone):
//   handler: internal/marketplace/handler/customer_handler_referral_balance.go
//
// spendable sums remaining_amount over the caller's LIVE redeemable awards
// (§4.4.1: remaining IS awarded − Σ COALESCE(settled, reserved), maintained
// transactionally — an open request's reservation is already subtracted, so
// this is the figure the amount-≤-balance gate compares against). holding
// sums remaining over the LIVE holding awards (§4.5: exists, not yet
// redeemable — shown alongside, never folded into spendable).

/**
 * The caller's referral credit balance split. Money arrives as DECIMAL
 * STRINGS with exactly two places (§43/§39 — never JSON numbers); the empty
 * state is "0.00"/"0.00", never null. by_status counts the live awards in
 * each arm of the split (terminal statuses are not balance availability).
 */
export interface ReferralBalanceWire {
  spendable: string; // decimal string — Σ remaining over redeemable-live
  holding: string; // decimal string — Σ remaining over holding-live
  by_status: { holding: number; redeemable: number }; // live award counts per arm
}

/**
 * Read the caller's referral balance (the redeem surface's denominator).
 *
 * GET /api/v1/marketplace/me/referral-balance → the ReferralBalanceWire
 * directly after the .data unwrap.
 *
 * Throws ApiError(401) when the customer session is gone — no zero-figure
 * fallback on auth loss (the panel degrades independently, the server's
 * balance check remains the authority on open).
 */
export async function getMyReferralBalance(): Promise<ReferralBalanceWire> {
  return embedApiFetch<ReferralBalanceWire>(BALANCE_PATH, { query: {} });
}

// ─── Attributed share links (account-area redesign Task 20 slice 2, ────────
//     migration 147) ─────────────────────────────────────────────────────────
//
//   handler: internal/marketplace/handler/customer_handler_referral_share_link.go
//     - HandleMyReferralShareLinkGet  (GET  /me/referral-share-link) — the
//       caller's ACTIVE link, `{"data":null}` when none exists (GET never
//       mints)
//     - HandleMyReferralShareLinkPost (POST /me/referral-share-link) —
//       create-or-rotate; IDEMPOTENT by contract (first POST mints, an
//       immediate re-POST returns the SAME token — no churn); rotation
//       happens only through the explicit `rotate:true` body flag
//     - HandleReferralLeadSubmit      (POST /referral-leads, PUBLIC/ungated)
//       — the ?ref= landing form's submission
//
// The token is an opaque URL-safe string (crypto/rand, base64url, 43 chars —
// never derived from customer data). The customer embed renders it VERBATIM
// in the share URL; it is not a secret from its owner, who must read it back
// to share it.

/** One attributed share link. Mirrors Go model.ReferralShareLink exactly
 *  (§39); created/updated/deleted_at are database-internal (rule 9) and
 *  absent from the wire. `version` is the rule-10 OCC token (informational
 *  on this surface — no If-Match client here; the service resolves
 *  create-vs-rotate server-side). */
export interface ShareLinkWire {
  id: string; // UUID
  customer_id: number;
  token: string; // opaque, URL-safe — the ?ref= value of the share URL
  version: number;
  /** TD-765 acceptance triple. Three states, and the distinction is load-bearing
   *  for the deploy window (the embed ships BEFORE the API): a string = accepted
   *  link; `null` = LEGACY UNACCEPTED link (its public leads are refused until
   *  the owner accepts); ABSENT (undefined) = an OLD server that predates the
   *  field — callers must never treat absent as unaccepted. */
  enrollment_disclosure_id?: string | null;
  enrollment_accepted_at?: string | null;
  enrollment_accepted_by_user_id?: number | null;
}

const SHARE_LINK_PATH = "/api/v1/marketplace/me/referral-share-link";
const REFERRAL_LEADS_PATH = "/api/v1/marketplace/referral-leads";

/**
 * The shape of a MINTED share-link token: 32 random bytes, base64url without
 * padding = exactly 43 characters of [A-Za-z0-9_-] (the Go service's
 * mintShareLinkToken, referral_share_link_service.go). The mount router uses
 * it to tell a share link from any other `?ref=` value — `ref` is a common
 * campaign parameter (`?ref=newsletter`) and must never hijack the catalog
 * (final fix wave I-2). Shape only: the server still resolves the token.
 */
export const SHARE_LINK_TOKEN_SHAPE = /^[A-Za-z0-9_-]{43}$/;

/**
 * Build the public share URL for a token: the CURRENT page's origin +
 * path with `?ref=<token>` — the mount page is where the landing form
 * renders, so the link re-derives its base from the live page rather than
 * any hard-coded host (no canonical marketplace-mount URL constant exists
 * client-side; the invitation flow's ACCOUNT_INVITATION_ACCEPT_BASE_URL is
 * likewise "the mount"). Existing query params are deliberately NOT carried
 * into the shared link — the shape is origin + path + exactly one param.
 */
export function referralShareLinkUrl(token: string): string {
  return `${window.location.origin}${window.location.pathname}?ref=${encodeURIComponent(token)}`;
}

/**
 * Read the caller's ACTIVE share link.
 *
 * GET /api/v1/marketplace/me/referral-share-link → the ShareLinkWire after
 * the .data unwrap, or NULL when the caller has none (`{"data":null}` — GET
 * never mints; creating is the POST's job).
 */
export async function getMyReferralShareLink(): Promise<ShareLinkWire | null> {
  const result = await embedApiFetch<ShareLinkWire | null>(SHARE_LINK_PATH, { query: {} });
  return result ?? null;
}

/**
 * Create the caller's share link, or rotate (revoke + mint) when `rotate` is
 * set. IDEMPOTENT by contract: a POST with an existing active link returns
 * THAT link unchanged — calling this twice never churns the token, so the
 * client needs no create-vs-exists probe and no If-Match.
 *
 * TD-765: `disclosureId` (the ACTIVE enrollment revision the member accepted)
 * is REQUIRED on every POST — create, rotate, and the re-POST that activates a
 * legacy unaccepted link (rotate=false: the server stamps the existing link and
 * KEEPS its token). Errors: 422 VALIDATION_ERROR (missing), 409
 * DISCLOSURE_STALE (not the active revision), 503 DISCLOSURE_NOT_CONFIGURED.
 *
 * POST /api/v1/marketplace/me/referral-share-link → the (created, re-read,
 * or rotated) ShareLinkWire.
 */
export async function createMyReferralShareLink(rotate: boolean, disclosureId: string): Promise<ShareLinkWire> {
  return embedApiFetch<ShareLinkWire>(SHARE_LINK_PATH, {
    method: "POST",
    body: { rotate, enrollment_disclosure_id: disclosureId },
  });
}

/**
 * The ?ref= landing form's submission. Mirrors the Go handler's
 * referralLeadRequestBody (§39): the share-link token plus the referred
 * business's contact fields — the SAME seven-required set as the
 * authenticated referral form (store name, contact first/last name, phone,
 * email, city, state). `recaptcha_token` rides ALWAYS: "" when the mount has
 * no site key (the keyless posture — the server bypasses when unconfigured),
 * the v3 token otherwise.
 */
export interface SubmitReferralLeadRequest {
  token: string; // the ?ref= value from the share URL
  store_name: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
  contact_email: string;
  description?: string; // optional — OMITTED when blank (the wire constraint)
  street?: string; // optional — OMITTED when blank
  city: string;
  state: string;
  recaptcha_token: string;
}

/** The public route's success body: deliberately opaque — the SAME `received`
 *  answer for a created lead and a deduped replay, so a replier cannot tell
 *  whether the (token, email) pair had already submitted (no membership
 *  oracle; the OWASP A01 posture extended to the success shape). */
export interface ReferralLeadReceipt {
  received: boolean;
}

/**
 * Submit the public referral lead (UNAUTHENTICATED — the route is ungated;
 * embedApiFetch attaches a Bearer only when a session token exists, which
 * the anonymous landing visitor has none of).
 *
 * POST /api/v1/marketplace/referral-leads → the ReferralLeadReceipt.
 *
 * Throws ApiError(404, NOT_FOUND) for EVERY referrer-side refusal — unknown
 * token, rotated/revoked token, an ineligible referrer, no
 * Aldelo Pay store — all deliberately ONE indistinguishable envelope
 * (the message is the server's "Referral link not found"). ApiError(403) for
 * a failed reCAPTCHA check; ApiError(422) for a blank required field;
 * ApiError(429) when an IP or token rate cap is hit (Retry-After on the
 * error payload's headers is the retry hint).
 */
export async function submitReferralLead(req: SubmitReferralLeadRequest): Promise<ReferralLeadReceipt> {
  const body: Record<string, unknown> = {
    token: req.token,
    store_name: req.store_name.trim(),
    contact_first_name: req.contact_first_name.trim(),
    contact_last_name: req.contact_last_name.trim(),
    contact_phone: req.contact_phone.trim(),
    contact_email: req.contact_email.trim(),
    city: req.city.trim(),
    state: req.state.trim(),
    recaptcha_token: req.recaptcha_token,
  };
  if (req.description?.trim()) body.description = req.description.trim();
  if (req.street?.trim()) body.street = req.street.trim();
  return embedApiFetch<ReferralLeadReceipt>(REFERRAL_LEADS_PATH, { method: "POST", body });
}
